import { z } from "zod";

import { simpleDateSchema } from "@/src/models/common-schema";
import { PriorAuthorizationDocumentType } from "@/src/models/constants/document-types";
import { ImagingType } from "@/src/models/constants/imaging-types";
import {
  MEDICATION_NAME_PROMPT,
  PRIOR_AUTHORIZATION_COMPANY_NAME_PROMPT,
  SUPPLY_DURATION_PROMPT,
} from "@/src/models/constants/prompt";
import { Specialty } from "@/src/models/constants/specialty";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { Location, locationSchema } from "@/src/models/location";
import { Patient, patientSchema } from "@/src/models/patient";
import { Practitioner, practitionerSchema } from "@/src/models/practitioner";
import type { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";

export enum PriorAuthorizationStatus {
  APPROVED = "approved",
  DENIED = "denied",
  FOLLOW_UP = "follow up",
  NO_AUTH_REQUIRED = "no auth required",
  NOT_RECEIVED = "not received",
  OTHER = "other",
  PARTIAL_APPROVAL = "partial approval",
  PEER_TO_PEER = "peer to peer",
  PENDING = "pending",
  REVIEW_NEEDED = "review needed",
  ESCALATED = "escalated",
}

export enum PriorAuthorizationRequestOrResponse {
  REQUEST = "request",
  RESPONSE = "response",
}

export enum PriorAuthorizationType {
  IMAGING = "imaging",
  MEDICATION = "medication",
  MEDICAL_SUPPLIES = "medical supplies",
  PEER_TO_PEER = "peer to peer",
  PROCEDURE = "procedure",
  REFERRAL = "referral",
  SERVICES = "services",
}

export const commonProcedureTerminologySchema = z.object({
  code: z.string(),
  description: z.string(),
  modifier: z.string().optional(),
});

export type CommonProcedureTerminologyItem = z.infer<typeof commonProcedureTerminologySchema>;

const basePriorAuthorizationFields = {
  patient: patientSchema,
  practitioner: practitionerSchema,
  receivingLocation: locationSchema.nullable(),
};

const extendedPriorAuthorizationSchema = z.object({
  requestId: z
    .string()
    .nullable()
    .describe(
      `The request ID or reference ID assigned to this prior authorization submission (often alphanumeric tracking ID from portal/Stedi).`
    ),
  authorizationNumber: z
    .string()
    .nullable()
    .describe(`The final approval number/certification code assigned to this prior authorization upon approval.`),
  documentType: z.string().nullable(),
  companyName: z.string().nullable().describe(PRIOR_AUTHORIZATION_COMPANY_NAME_PROMPT),
  authorizationType: z.nativeEnum(PriorAuthorizationType).nullable(),
  authorizationStatus: z.nativeEnum(PriorAuthorizationStatus).nullable(),
  authorizationFor: z.string().nullable(),
  commonProcedureTerminologyList: z
    .array(commonProcedureTerminologySchema)
    .nullable()
    .describe(
      "Procedure or service lines as CPT/HCPCS-style entries: one object per line with code, full text description, and optional modifier when shown. Use null if no billable procedure codes appear on the document."
    ),
  startDate: simpleDateSchema.nullable(),
  endDate: simpleDateSchema.nullable(),
  numberOfServices: z.number().nullable().describe(`
    The number of services authorized, approved, allowed, or requested. Treat "visits", "services", "sessions", "treatments", and "units" as equivalent labels for this count. For example, "visits allowed: 99" means numberOfServices is 99. Extract this value even when the same count is also extracted as numberOfVisits.

    DO NOT extract:
    - Page numbers
    - Line numbers
    - Numbers near corners of pages
  `),
  specialty: z.nativeEnum(Specialty).nullable(),
  procedure: z.string().nullable(),
  medicationName: z.string().nullable().describe(MEDICATION_NAME_PROMPT),
  medications: z.array(z.string().describe(MEDICATION_NAME_PROMPT)).nullable(),
  supplyDuration: z.string().nullable().describe(SUPPLY_DURATION_PROMPT),
  location: z.string().nullable(),
  signatureRequired: z.boolean().nullable(),
  imagingType: z.nativeEnum(ImagingType).nullable(),
  requestOrResponse: z.nativeEnum(PriorAuthorizationRequestOrResponse).nullable(),
  bodyPart: z.string().nullable(),
  bodyPartLaterality: z.string().nullable(),
  ...DocumentDateSchema.shape,
});

export const referralPriorAuthorizationFieldsSchema = extendedPriorAuthorizationSchema.pick({
  requestId: true,
  authorizationNumber: true,
  authorizationType: true,
  authorizationStatus: true,
  authorizationFor: true,
  commonProcedureTerminologyList: true,
  numberOfServices: true,
  startDate: true,
  endDate: true,
});

export const priorAuthorizationTypeDisplay: Record<PriorAuthorizationDocumentType, string> = {
  [PriorAuthorizationDocumentType.IMAGING_REQUEST]: "Prior Auth Imaging Request",
  [PriorAuthorizationDocumentType.IMAGING_RESPONSE]: "Prior Auth Imaging Response",
  [PriorAuthorizationDocumentType.MEDICATION_REQUEST]: "Prior Auth Medication Request",
  [PriorAuthorizationDocumentType.MEDICATION_RESPONSE]: "Prior Auth Medication Response",
  [PriorAuthorizationDocumentType.PRIOR_AUTHORIZATION]: "Prior Auth",
  [PriorAuthorizationDocumentType.PROCEDURE_REQUEST]: "Prior Auth Procedure Request",
  [PriorAuthorizationDocumentType.PROCEDURE_RESPONSE]: "Prior Auth Procedure Response",
  [PriorAuthorizationDocumentType.REFERRAL_REQUEST]: "Prior Auth Referral Request",
  [PriorAuthorizationDocumentType.REFERRAL_RESPONSE]: "Prior Auth Referral Response",
  [PriorAuthorizationDocumentType.SURGERY_REQUEST]: "Prior Auth Surgery Request",
  [PriorAuthorizationDocumentType.SURGERY_RESPONSE]: "Prior Auth Surgery Response",
  [PriorAuthorizationDocumentType.THERAPY_REQUEST]: "Prior Auth Therapy Request",
  [PriorAuthorizationDocumentType.THERAPY_RESPONSE]: "Prior Auth Therapy Response",
};

const priorAuthorizationCustomFields: Record<PriorAuthorizationDocumentType, Record<string, z.ZodTypeAny>> = {
  [PriorAuthorizationDocumentType.PRIOR_AUTHORIZATION]: extendedPriorAuthorizationSchema.pick({
    requestId: true,
    authorizationNumber: true,
    authorizationType: true,
    authorizationStatus: true,
    authorizationFor: true,
    commonProcedureTerminologyList: true,
    startDate: true,
    endDate: true,
    numberOfServices: true,
    dateOfDocument: true,
    dateReceived: true,
    medicationName: true,
    requestOrResponse: true,
  }).shape,
  [PriorAuthorizationDocumentType.MEDICATION_REQUEST]: extendedPriorAuthorizationSchema.pick({
    dateOfEvent: true,
    dateReceived: true,
    companyName: true,
    medicationName: true,
    medications: true,
    supplyDuration: true,
    signatureRequired: true,
  }).shape,
  [PriorAuthorizationDocumentType.MEDICATION_RESPONSE]: extendedPriorAuthorizationSchema.pick({
    dateOfEvent: true,
    dateReceived: true,
    medicationName: true,
    medications: true,
    supplyDuration: true,
    authorizationStatus: true,
    signatureRequired: true,
  }).shape,
  [PriorAuthorizationDocumentType.PROCEDURE_REQUEST]: extendedPriorAuthorizationSchema.pick({
    authorizationType: true,
    commonProcedureTerminologyList: true,
    dateOfEvent: true,
    dateReceived: true,
    procedure: true,
    documentType: true,
  }).shape,
  [PriorAuthorizationDocumentType.PROCEDURE_RESPONSE]: extendedPriorAuthorizationSchema.pick({
    documentType: true,
    authorizationType: true,
    authorizationStatus: true,
    commonProcedureTerminologyList: true,
    dateReceived: true,
    procedure: true,
    specialty: true,
    signatureRequired: true,
  }).shape,
  [PriorAuthorizationDocumentType.REFERRAL_REQUEST]: extendedPriorAuthorizationSchema.pick({
    dateReceived: true,
    specialty: true,
  }).shape,
  [PriorAuthorizationDocumentType.REFERRAL_RESPONSE]: extendedPriorAuthorizationSchema.pick({
    dateOfEvent: true,
    dateReceived: true,
    specialty: true,
    authorizationStatus: true,
  }).shape,
  [PriorAuthorizationDocumentType.IMAGING_REQUEST]: extendedPriorAuthorizationSchema.pick({
    dateReceived: true,
    companyName: true,
    bodyPartLaterality: true,
    imagingType: true,
  }).shape,
  [PriorAuthorizationDocumentType.IMAGING_RESPONSE]: extendedPriorAuthorizationSchema.pick({
    dateReceived: true,
    authorizationStatus: true,
    imagingType: true,
    bodyPart: true,
    bodyPartLaterality: true,
  }).shape,
  [PriorAuthorizationDocumentType.SURGERY_REQUEST]: extendedPriorAuthorizationSchema.pick({
    dateReceived: true,
    companyName: true,
  }).shape,
  [PriorAuthorizationDocumentType.SURGERY_RESPONSE]: extendedPriorAuthorizationSchema.pick({
    dateReceived: true,
    authorizationStatus: true,
  }).shape,
  [PriorAuthorizationDocumentType.THERAPY_REQUEST]: extendedPriorAuthorizationSchema.pick({
    dateReceived: true,
    companyName: true,
  }).shape,
  [PriorAuthorizationDocumentType.THERAPY_RESPONSE]: extendedPriorAuthorizationSchema.pick({
    dateReceived: true,
    authorizationStatus: true,
  }).shape,
};

const makePriorAuthorizationSchema = (
  type: PriorAuthorizationDocumentType,
  customFields: Record<string, z.ZodTypeAny>
) =>
  z.object({
    type: z.literal(type),
    ...basePriorAuthorizationFields,
    ...customFields,
  });

export const priorAuthorizationSchemas: Record<PriorAuthorizationDocumentType, z.ZodObject<any>> = (
  Object.values(PriorAuthorizationDocumentType) as PriorAuthorizationDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makePriorAuthorizationSchema(type, priorAuthorizationCustomFields[type]);

    return acc;
  },
  {} as Record<PriorAuthorizationDocumentType, z.ZodObject<any>>
);

const priorAuthorizationSchemasArray = (
  Object.values(PriorAuthorizationDocumentType) as PriorAuthorizationDocumentType[]
).map((type) => priorAuthorizationSchemas[type]) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyPriorAuthorizationSchema = z.discriminatedUnion("type", priorAuthorizationSchemasArray);

export type PriorAuthorization = z.infer<typeof anyPriorAuthorizationSchema> & {
  section: Section | null;
  patient: Patient | null;
  practitioner: Practitioner | null;
  receivingLocation: Location | null;
  displayId: string | null;
} & DatabaseDefaults;

export type PriorAuthorizationLinks = {
  taskDisplayId: string | null;
  referralDisplayId: string | null;
};

export type PaginatedPriorAuthorization = PriorAuthorization &
  PriorAuthorizationLinks & {
    patientName: string | null;
    serviceSummary: string | null;
  };

export type PriorAuthorizationDetail = PriorAuthorization &
  PriorAuthorizationLinks & {
    tasks: Task[];
    sourceTaskId: string | null;
  };

export type PriorAuthorizationNavigatorItem = Pick<PriorAuthorization, "id" | "displayId" | "createdAt" | "type">;

const now = new Date().toISOString();

export const priorAuthorizationShape: PriorAuthorization = {
  id: "",
  section: null,
  patient: null,
  practitioner: null,
  receivingLocation: null,
  displayId: null,
  type: PriorAuthorizationDocumentType.PRIOR_AUTHORIZATION,
  requestId: null,
  authorizationNumber: null,
  authorizationType: null,
  authorizationStatus: null,
  authorizationFor: null,
  commonProcedureTerminologyList: null,
  startDate: null,
  endDate: null,
  numberOfServices: null,
  dateOfDocument: null,
  dateOfEvent: null,
  medicationName: null,
  supplyDuration: null,
  location: null,
  specialty: null,
  procedure: null,
  dateReceived: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
